import styled from '@emotion/styled';

import { TransactionOnchainStatus } from '@/api/transaction-views/queries';
import ExternalLinkIcon from '@/assets/svg/ui-elements/external-link.svg';
import {
  forMobile,
  hoverable,
  IconButton,
  Link,
  type LinkProps,
  Typography,
} from '@/design-system';

export const Body = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[24],
}));

export const Summary = styled('section')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[8],
  padding: theme.spacing[20],
  borderRadius: theme.radius[12],
  backgroundColor: theme.colors.background.canvas,
}));

export const SummaryMeta = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing[8],
}));

export const DirectionLabel = styled(Typography)(({ theme }) => ({
  color: theme.colors.text.primary,
  textTransform: 'uppercase',
}));

export const StatusLabel = styled(Typography, {
  shouldForwardProp: (property) => property !== '$status',
})<{ $status: TransactionOnchainStatus }>(({ theme, $status }) => ({
  color:
    $status === TransactionOnchainStatus.Success
      ? theme.colors.success.text
      : theme.colors.error.text,
  textTransform: 'uppercase',
}));

export const SummarySeparator = styled(Typography)(({ theme }) => ({
  color: theme.colors.text.tertiary,
}));

export const SummaryContent = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[8],
  minWidth: 0,
}));

export const SummaryAmount = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing[8],
  minWidth: 0,
}));

export const SummaryValue = styled(Typography)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing[8],
  maxWidth: '100%',
  overflowWrap: 'anywhere',
}));

export const SummaryUsd = styled(Typography)(({ theme }) => ({
  color: theme.colors.text.secondary,
}));

export const SummaryDate = styled(Typography)(({ theme }) => ({
  color: theme.colors.text.secondary,
}));

export const Facts = styled('dl')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: theme.spacing[12],
  margin: 0,
  padding: 0,
  [forMobile]: {
    gridTemplateColumns: 'minmax(0, 1fr)',
  },
}));

export const Fact = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[4],
  minWidth: 0,
  padding: `${theme.spacing[12]}px ${theme.spacing[16]}px`,
  borderRadius: theme.radius[8],
  backgroundColor: theme.colors.background.canvas,
}));

export const TransferSection = styled('section')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[8],
}));

export const TransferPath = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
  alignItems: 'center',
  gap: theme.spacing[12],
  padding: theme.spacing[16],
  border: `1px solid ${theme.colors.border.default}`,
  borderRadius: theme.radius[12],
  [forMobile]: {
    gridTemplateColumns: 'minmax(0, 1fr)',
  },
}));

export const TransferEndpoint = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[4],
  margin: 0,
  minWidth: 0,
}));

export const TransferArrow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: theme.spacing[32],
  height: theme.spacing[32],
  borderRadius: theme.radius.full,
  backgroundColor: theme.colors.brand.subtle,
  color: theme.colors.brand.text,
  '& svg': { width: theme.spacing[16], height: theme.spacing[16] },
  [forMobile]: { transform: 'rotate(90deg)' },
}));

export const FactLabel = styled(Typography)(({ theme }) => ({
  color: theme.colors.text.tertiary,
  textTransform: 'uppercase',
}));

export const FactValue = styled(Typography)({
  minWidth: 0,
  overflowWrap: 'anywhere',
});

export const AddressDisplay = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  flexDirection: 'column',
  minWidth: 0,
  maxWidth: '100%',
  gap: theme.spacing[2],
  overflow: 'hidden',
}));

type ExternalLinkProps = Extract<LinkProps, { href: string }>;

const AddressLinkBase = (props: ExternalLinkProps) => <Link {...props} />;

export const AddressLink = styled(AddressLinkBase)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  minWidth: 0,
  maxWidth: '100%',
  width: 'fit-content',
  padding: 0,
  gap: theme.spacing[4],
  overflow: 'hidden',
  textAlign: 'left',
}));

export const ExternalIcon = styled(ExternalLinkIcon)({
  flexShrink: 0,
});

export const DigestSection = styled('section')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[8],
  padding: 0,
}));

export const SectionLabel = styled(Typography)(({ theme }) => ({
  color: theme.colors.text.tertiary,
  textTransform: 'uppercase',
}));

export const DigestRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing[12],
  minWidth: 0,
  padding: theme.spacing[12],
  borderRadius: theme.radius[8],
  backgroundColor: theme.colors.background.overlay,
  [forMobile]: {
    alignItems: 'stretch',
    flexDirection: 'column',
    gap: theme.spacing[8],
  },
}));

export const Digest = styled(Typography)({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const DigestValue = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  flex: 1,
  minWidth: 0,
  gap: theme.spacing[4],
}));

export const DigestActions = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  gap: theme.spacing[8],
  [forMobile]: {
    justifyContent: 'stretch',
    width: '100%',
  },
}));

export const DigestCopyButton = styled(IconButton)(({ theme }) => ({
  flexShrink: 0,
  [forMobile]: {
    minWidth: theme.spacing[48],
    minHeight: theme.spacing[48],
    border: 'none',
  },
}));

export const DigestActionLink = styled(AddressLinkBase)(({ theme }) => ({
  ...hoverable(
    {
      ...theme,
      colors: {
        ...theme.colors,
        state: { ...theme.colors.state, hover: theme.colors.neutral.subtleHover },
      },
    },
    'surface',
  ),
  display: 'inline-flex',
  alignItems: 'center',
  width: 'fit-content',
  minHeight: theme.spacing[40],
  padding: `0 ${theme.spacing[12]}px`,
  gap: theme.spacing[4],
  borderRadius: theme.radius[8],
  backgroundColor: theme.colors.neutral.subtle,
  color: theme.colors.text.secondary,
  '&&&': {
    textDecoration: 'none',
  },
  [forMobile]: {
    justifyContent: 'center',
    width: '100%',
    minWidth: theme.spacing[48],
    minHeight: theme.spacing[48],
  },
}));

export const Loading = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: theme.spacing[80],
}));
